import React from "react";

import { useInfiniteQuery } from "@tanstack/react-query";

import ProjectsService from "@/services/ProjectsService";
import ViewsService from "@/services/ViewsService";

import { useActiveProject } from "@/hooks/useActiveProject";
import { useCanWriteTasks } from "@/hooks/useActiveRole";
import { useActiveView } from "@/hooks/useActiveView";
import { useContextMenus } from "@/hooks/useContextMenus";
import { useHomeLocation } from "@/hooks/useHomeLocation";
import { useTasksFilters } from "@/hooks/useTasksFilters";

import { TasksListCtxMenu } from "@/components/menus/TasksListCtxMenu";
import { DayHeader, TasksListHeader } from "@/components/molecules";
import { InfiniteList } from "@/components/molecules";
import { TaskListItem } from "@/components/molecules";

import { getMonthRange, isFullMonth } from "@/utils/UDates";
import { cn } from "@/utils/cn";

import { taskKeys } from "@/constants/CQueryKeys";
import { ResourceRole } from "@/constants/CRoles";

import { TTask, TTasksGroup } from "@/types";

import { SelectionProvider, TSelectionContextType } from "@/contexts";
import { useAuth } from "@/hooks";
import { isSup } from "@/utils";

export interface ITasksListProps {
	onItemClick?: (task: TTask) => void;
	header?: boolean;
	scroller?: string | HTMLElement;
	className?: string;
	selectable?: boolean;
	itemActions?: boolean;
}

export const TasksList: React.FC<ITasksListProps> = ({
	onItemClick,
	header = true,
	scroller,
	className,
	selectable = false,
	itemActions = true,
}) => {
	const { view: activeView } = useActiveView();
	const { project: activeProject } = useActiveProject();
	const { taskId: activeTaskId } = useHomeLocation();
	const { filters } = useTasksFilters();
	const { user } = useAuth();

	const {
		tasksList: tasksListCtxMenu,
		projects: projectsCtxMenu,
		views: viewsCtxMenu,
	} = useContextMenus();

	const viewId = activeView?.id;
	const projectId = activeProject?.id;
	// A VIEWER of the filtered project can read but not write: the API's
	// TaskVoter refuses every mutation, so the item menu (edit / duplicate
	// / delete) and the multi-select are not offered rather than failing
	// on use.
	const canWrite = useCanWriteTasks();

	const fetchTasks = (page?: number) => {
		const defaultRange = getMonthRange();
		const payload = {
			...filters,
			page: page ?? filters.page,
			grouped: true,
			dateFrom: filters.dateFrom ?? defaultRange.dateFrom,
			dateTo: filters.dateTo ?? defaultRange.dateTo,
		};
		if (projectId)
			return ProjectsService.tasksOfProject(projectId, {
				...payload,
				projectId,
			});
		if (viewId)
			return ViewsService.getViewTasks(viewId, {
				...payload,
				viewId,
			});
		return Promise.resolve([]);
	};

	const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } =
		useInfiniteQuery({
			queryKey: taskKeys.list(viewId, projectId, filters),
			queryFn: (query) => fetchTasks(query?.pageParam?.page),
			enabled: !!viewId,
			getNextPageParam: (lastPage) => {
				const nextPage = lastPage.page + 1;
				return nextPage <= Math.ceil(lastPage.total / lastPage.limit)
					? { page: nextPage, limit: lastPage.limit }
					: undefined;
			},
			initialPageParam: { page: filters.page, limit: filters.limit },
		});

	const handleContextMenu = (e: React.MouseEvent, task: TTask) => {
		if (e.altKey) return;
		tasksListCtxMenu.show(e, { ...task });
	};

	const handleTasksListHeaderMenuClick = (
		event: React.MouseEvent,
		selectionContext: TSelectionContextType<TTask>
	) => {
		if (activeProject?.id) {
			if (!isSup(ResourceRole.USER, activeProject.role)) {
				event.preventDefault();
				return;
			}
			projectsCtxMenu.show(event, { ...activeProject });
		} else if (activeView?.id) {
			viewsCtxMenu.show(event, {
				...activeView,
				from: "taskList",
				onSelect: () =>
					selectionContext.setSelectMode(
						!selectionContext.selectMode
					),
			});
		}
	};

	const fullMonth =
		filters.dateFrom &&
		filters.dateTo &&
		isFullMonth(filters.dateFrom, filters.dateTo);

	const pages = data?.pages.map((page) => page.items ?? []) ?? [];
	const selectableItems =
		pages
			.flatMap((page) =>
				page.flatMap((group: TTasksGroup) => group.tasks)
			)
			.filter((task: TTask) => task.userId === user?.id) ?? [];
	const totalCount = data?.pages[0]?.total;

	return (
		<SelectionProvider
			getKey={(task: TTask) => task.id}
			selectable={
				selectableItems.length > 0 && selectable && canWrite
					? selectableItems
					: []
			}
		>
			{(SelectionContext) => (
				<>
					{header && (
						<TasksListHeader
							title={activeProject?.name || activeView?.name}
							scroller={scroller}
							count={totalCount}
							isLoading={isLoading}
							isArchived={
								activeProject?.isArchived ||
								activeView?.isArchived
							}
							isReadOnly={
								activeProject?.role === ResourceRole.VIEWER
							}
							onMenuClick={
								// The view's own menu is always the owner's;
								// a project's is gated on what it grants.
								!activeProject ||
								isSup(ResourceRole.USER, activeProject.role)
									? (e) =>
											handleTasksListHeaderMenuClick(
												e,
												SelectionContext
											)
									: undefined
							}
							className="task-list-header z-10 bg-white"
						/>
					)}
					<InfiniteList<TTasksGroup>
						pages={pages}
						keyExtractor={(group) => group.date}
						fetchNextPage={fetchNextPage}
						hasNextPage={hasNextPage ?? false}
						isFetchingNextPage={isFetchingNextPage}
						isLoading={isLoading}
						className={cn(["flex flex-col gap-8", className])}
						renderItem={(tasksGroup) => (
							<div>
								<DayHeader
									date={tasksGroup.date}
									nbrTasks={tasksGroup.total}
									duration={{
										duration: tasksGroup.workDuration,
										dateFrom: null,
									}}
									isLoading={isFetchingNextPage}
								/>
								<div className="mt-2 flex flex-col gap-1">
									{tasksGroup.tasks.map((task) => (
										<TaskListItem
											key={task.id}
											task={task}
											onClick={onItemClick}
											onContextMenu={(e) => {
												if (!e.altKey)
													e.preventDefault();
												return handleContextMenu(
													e,
													task
												);
											}}
											isActive={activeTaskId === task.id}
											actions={itemActions && canWrite}
											selectable={selectable && canWrite}
										/>
									))}
								</div>
							</div>
						)}
						noDataMessage={
							filters.dateFrom ? (
								<DayHeader
									isLoading={isLoading}
									date={filters.dateFrom}
									format={fullMonth ? "MMMM yyyy" : undefined}
								/>
							) : undefined
						}
					/>
					<TasksListCtxMenu />
				</>
			)}
		</SelectionProvider>
	);
};
